import * as React from 'react';
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Alert } from '@vivaahealth/design-system';

import * as api from '../../../api';

import styles from './index.module.css';

import { Button } from '../../Button';
import { TextInput } from '../../TextInput';

import { TRUNCATE_LENGTH } from '../';

import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const ADDRESS_ONE_LABEL = 'Address Line 1';
const ADDRESS_TWO_LABEL = 'Address Line 2';
const CITY_LABEL = 'City';
const STATE_LABEL = 'State';
const ZIP_CODE_LABEL = 'Zip Code';

const requiredFields = {
  addressOne: ADDRESS_ONE_LABEL,
  city: CITY_LABEL,
  state: STATE_LABEL,
  zipCode: ZIP_CODE_LABEL,
};

interface Props extends WithTranslation {
  place: api.Places.PlaceDetails;
  onSave: (place: api.Places.PlaceDetails) => void;
  pending?: boolean;
  missingUnit?: boolean;
  error?: string;
}

interface State {
  selectedPlace: api.Places.PlaceDetails;
  errorMessage: string;
  missingUnit?: boolean;
}

export const isValidZipcode = (zipcode: string) => {
  const trimmedZipcode = zipcode.trim();
  return /^[0-9]{5}(?:-[0-9]{4})?$/.test(trimmedZipcode);
};

class ConfirmAddress extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedPlace: props.place,
      errorMessage: props.error || '',
      missingUnit: props.missingUnit,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { place, error, missingUnit } = this.props;

    if (prevProps.place !== place) {
      this.setState({
        selectedPlace: place,
      });
    }

    if (prevProps.error !== error) {
      this.setState({ errorMessage: error || '' });
    }
    if (prevProps.missingUnit !== missingUnit) {
      this.setState({ missingUnit });
    }
  }

  setSelectedPlaceField = (name: string, value: string) => {
    const selectedPlace = Object.assign({}, this.state.selectedPlace, {
      [name]: value,
    });

    this.setState({ selectedPlace });
  };

  setTextField = (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.currentTarget;
    if (value.length > TRUNCATE_LENGTH) return;

    this.setSelectedPlaceField(name, value.toUpperCase());
  };

  setSelectField = (event: HTMLInputElement) => {
    const { name, value } = event;
    this.setSelectedPlaceField(name, value);
  };

  isInputError = (trimmed: api.Places.PlaceDetails): boolean => {
    const { t } = this.props;
    const missingField = _.some(requiredFields, (v, k) => {
      if (!trimmed[k as keyof api.Places.PlaceDetails]) {
        this.setState({
          errorMessage: t('errorMessage', { input: t(v) }),
        });
        return true;
      }
      return false;
    });
    if (missingField) return true;
    if (trimmed.addressOne.length < 5 || trimmed.addressOne.includes('@')) {
      this.setState({ errorMessage: t('invalidAddress') });
      return true;
    }
    if (!isValidZipcode(trimmed.zipCode)) {
      this.setState({ errorMessage: t('invalidZipcode') });
      return true;
    }
    return false;
  };

  saveAddress = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const { onSave } = this.props;
    const { selectedPlace } = this.state;

    let trimmed: api.Places.PlaceDetails = {
      addressOne: selectedPlace.addressOne.trim(),
      addressTwo: selectedPlace.addressTwo.trim(),
      city: selectedPlace.city.trim(),
      latitude: selectedPlace.latitude,
      longitude: selectedPlace.longitude,
      state: selectedPlace.state.trim(),
      zipCode: selectedPlace.zipCode.trim(),
    };
    if (this.isInputError(trimmed)) {
      return;
    }

    onSave(trimmed);
    this.setState({ errorMessage: '' });
  };

  render() {
    const { selectedPlace: place, errorMessage } = this.state;
    const { addressOne, addressTwo, city, state, zipCode } = place || {};
    const { t, missingUnit } = this.props;

    return (
      <div>
        {errorMessage ? (
          <div className={styles.InfoMessage}>
            <Alert variant="danger">{errorMessage}</Alert>
          </div>
        ) : null}
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <label htmlFor="address-line-one" className={styles.label}>
                    {t(ADDRESS_ONE_LABEL)}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    id="address-line-one"
                    autoFocus={true}
                    name="addressOne"
                    value={addressOne}
                    onChange={this.setTextField}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <label className={styles.label} htmlFor="address-line-two">
                    {t(ADDRESS_TWO_LABEL)}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    id="address-line-two"
                    name="addressTwo"
                    value={addressTwo}
                    onChange={this.setTextField}
                    error={missingUnit}
                    helperText={
                      missingUnit && t('addressInput::missingUnitError')
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <label className={styles.label} htmlFor="address-city">
                    {t(CITY_LABEL)}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    id="address-city"
                    name="city"
                    value={city}
                    onChange={this.setTextField}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <label className={styles.label} htmlFor="address-state">
                        {t(STATE_LABEL)}
                      </label>
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        id="address-state"
                        className={styles.select}
                        name="state"
                        value={state}
                        onChange={(e) =>
                          e &&
                          this.setSelectField(
                            e.currentTarget as HTMLInputElement,
                          )
                        }
                        fullWidth
                        variant="outlined"
                        native
                        IconComponent={KeyboardArrowDownIcon}
                        inputProps={{ style: { padding: '12px 20px' } }}
                      >
                        <option value="" />
                        {Object.keys(
                          api.PrevisitAddress.UsStates.US_STATES_MAPPING,
                        ).map((abrv) => (
                          <option key={abrv} value={abrv}>
                            {abrv}
                          </option>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <label className={styles.label} htmlFor="address-zip">
                        {t(ZIP_CODE_LABEL)}
                      </label>
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        id="address-zip"
                        name="zipCode"
                        value={zipCode}
                        onChange={this.setTextField}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} className={styles.button}>
              <Button
                children={t('Save')}
                onClick={this.saveAddress}
                disabled={this.props.pending}
              />
            </Grid>
          </Grid>
        </form>
      </div>
    );
  }
}

export default withTranslation()(ConfirmAddress);
